/**
 * The approval email (Kaleigh Vazquez & Josie Evans, "PA Sale to Approval - IF THIS THEN THAT", 2026-10-08;
 * Isaiah Cotto, 2026-10-13).
 *
 *   "This is the time that Josie sends APPROVAL EMAILS. Josie to Verify (a checklist would be nice for verification
 *    to generate an email with the correct attachments and information): Submitted Terms; Who Manages the Funds;
 *    POA; Facility (BOM, SSD, ABOM, ADMIN)."
 *
 * Josie ticks the four checklist items, chooses who it goes to (pre-ticked by who manages the funds), confirms each
 * document it must carry, edits the words, and sends -- from eMerge when the server says sending is on, or as a file
 * she opens in Outlook and sends herself. eMerge never sends on its own. Anything missing blocks sending and says
 * why in plain words; the server checks everything again.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { approvalEmailApi, freshKey, shortDateTime } from './umedApi';
import type { ApprovalEmailChecklist, ApprovalEmailDraft, ApprovalEmailResult, ApprovalEmailSendBody, ApiResult } from './umedApi';

type Item = 'terms' | 'funds' | 'poa' | 'facility';
const ITEMS: Array<[Item, string]> = [['terms', 'Submitted terms'], ['funds', 'Who manages the funds'], ['poa', 'POA'], ['facility', 'Facility (BOM, ABOM, SSD, Administrator)']];

/** What a refusal means, said plainly. The server's own code decides which sentence. */
export function refusalText(r: Pick<ApiResult<unknown>, 'status' | 'code' | 'message' | 'error'>): string {
  if (r.status === undefined) return 'Could not reach the server. Nothing was sent.';
  const byCode: Record<string, string> = {
    NOT_APPROVED: 'The approval email is sent once the case is approved in eMerge.',
    NOT_IN_APPROVAL_SCOPE: 'You can’t send approval emails for this resident’s building.',
    CHECKLIST_NOT_CONFIRMED: 'Tick every checklist item before sending.',
    CHECKLIST_GAPS: 'Something the email needs is missing. Read the list above.',
    CHECKLIST_CHANGED: 'Something on the checklist changed since you opened it. It has been reloaded; review it again.',
    NO_RECIPIENTS: 'Choose at least one recipient.',
    RECIPIENT_NOT_ON_RECORD: 'Every recipient must be on record for this case. Record the contact first.',
    RECIPIENT_SUPPRESSED: 'One of the addresses has bounced or complained before. Remove it, or use Download for Outlook.',
    ATTACHMENT_UNCONFIRMED: 'Confirm every document the email must carry.',
    ATTACHMENT_NOT_THIS_RESIDENT: 'A document does not belong to this resident’s case. Nothing was sent.',
    ATTACHMENT_NOT_REQUIRED: 'Only the documents the funds manager calls for may be attached.',
    ALREADY_SENT: 'An approval email was already sent (or may have been) for this case. Confirm below that you mean to send another.',
    SEND_IN_FLIGHT: 'An approval email for this case is being sent right now. Wait a moment.',
    SEND_DISABLED: 'Sending from eMerge is off. Use Download for Outlook.',
    SEND_TRANSPORT_UNAVAILABLE: 'eMerge cannot send mail here. Use Download for Outlook.',
    MESSAGE_TOO_LARGE: 'The attachments are too large to send from eMerge. Use Download for Outlook.',
    SENDER_EMAIL_MISSING: 'Your account has no email address, so replies would have nowhere to go.',
    PLACEHOLDER_LEFT: 'A {{placeholder}} is still in the email. Replace it first.',
    SUBJECT_INVALID: 'The subject is one line of up to 250 characters.',
    BODY_INVALID: 'The email needs words.',
    APPROVAL_EMAIL_MIGRATION_REQUIRED: 'The approval email is not set up on this server yet.',
    OUTCOME_NOT_RECORDED: 'eMerge could not record what happened to the email. Do not send it again until someone checks.',
  };
  if (r.code && byCode[r.code]) return byCode[r.code];
  if (r.status === 403) return 'Not permitted.';
  if (r.status === 404) return 'Not switched on here.';
  return 'The server refused this. Nothing was sent.';
}

const money = (cents: number | null) => (cents === null || cents === undefined ? null : `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);

/** PR #416: an attachment marked "Not this resident's" while the email was being sent. The email went as listed. */
function markedDuringSendText(r: Pick<ApprovalEmailResult, 'state'> & { markedDuringSend?: Array<{ document_class: string }> }): string {
  const marked = r.markedDuringSend || [];
  if (!marked.length) return '';
  const which = `Warning: ${marked.map((m) => m.document_class).join(', ')} was marked "Not this resident's" while this email was being sent.`;
  // UNKNOWN: eMerge could not confirm the email went, so it never says the recipients received it.
  return r.state === 'UNKNOWN'
    ? ` ${which} If it was sent, the recipients have this document: once you confirm it was sent, tell them which document to disregard and send this resident's own.`
    : ` ${which} The recipients received it; tell them which document to disregard and send this resident's own.`;
}

function resultText(r: ApprovalEmailResult): string {
  const to = (r.to || []).map((x) => x.address).join(', ');
  switch (r.state) {
    case 'SENT': return `Approval email sent to ${to}.${r.replayed ? ' (Already sent; nothing was sent twice.)' : ''}${markedDuringSendText(r)}`;
    case 'UNKNOWN': return `eMerge could not confirm whether the email was sent (the mail service did not answer). Check with the recipients before sending again.${markedDuringSendText(r)}`;
    case 'FAILED': return `The email was NOT sent${r.outcomeCode ? ` (${r.outcomeCode})` : ''}. Nothing reached the recipients.`;
    case 'PREPARED_FOR_OUTLOOK': return 'Downloaded for Outlook. Open the file, check it, and send it from Outlook. eMerge did not send it.';
    default: return 'The email is being sent. Reload in a moment.';
  }
}

/** Hand the browser the .eml. Returns false where the browser cannot save a file (it is then not claimed as saved). */
function saveEml(r: ApprovalEmailResult): boolean {
  if (!r.emlBase64 || typeof window === 'undefined' || !window.URL || typeof window.URL.createObjectURL !== 'function') return false;
  const raw = atob(r.emlBase64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) bytes[i] = raw.charCodeAt(i);
  const href = window.URL.createObjectURL(new Blob([bytes], { type: 'message/rfc822' }));
  const a = document.createElement('a');
  a.href = href;
  a.download = r.filename || 'approval-email.eml';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => { if (typeof window.URL.revokeObjectURL === 'function') window.URL.revokeObjectURL(href); }, 0);
  return true;
}

export default function ApprovalEmailPanel({ caseId, openRequest = 0, onChanged }: {
  caseId: string;
  /** Raised (a fresh number) to open the panel and bring it into view: right after an approval. */
  openRequest?: number;
  onChanged?: (msg: string) => void | Promise<void>;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [cl, setCl] = useState<ApprovalEmailChecklist | null>(null);
  const [draft, setDraft] = useState<ApprovalEmailDraft | null>(null);
  const [ticked, setTicked] = useState<Record<Item, boolean>>({ terms: false, funds: false, poa: false, facility: false });
  const [to, setTo] = useState<Set<string>>(new Set());
  const [docs, setDocs] = useState<Record<string, string | null>>({});
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [resend, setResend] = useState(false);
  const [busy, setBusy] = useState<'SEND' | 'OUTLOOK' | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const key = useRef<string>(freshKey());
  const heading = useRef<HTMLHeadingElement | null>(null);
  const section = useRef<HTMLDivElement | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setLoadErr(null);
    try {
      const [c, d] = await Promise.all([approvalEmailApi.checklist(caseId), approvalEmailApi.draft(caseId)]);
      if (!c.success || !c.data) { setLoadErr(refusalText(c)); return; }
      setCl(c.data);
      setTo(new Set(c.data.recipients.filter((r) => r.pre_selected && r.email).map((r) => r.email as string)));
      setDocs(Object.fromEntries(c.data.required_attachments.map((a) => [a.document_class, null])));
      setTicked({ terms: false, funds: false, poa: false, facility: false });
      setResend(false);
      if (d.success && d.data) { setDraft(d.data); setSubject(d.data.subject); setBody(d.data.body); }
    } catch {
      setLoadErr('Could not reach the server.');
    } finally { setLoading(false); }
  }, [caseId]);

  // A different case is a different email.
  useEffect(() => { setOpen(false); setCl(null); setDraft(null); setDone(null); setErr(null); key.current = freshKey(); }, [caseId]);
  useEffect(() => { if (open) void load(); }, [open, load]);
  // Opened from an approval: open, and bring it into view. Focus stays where the act left it (the row's name, P1-b);
  // the heading is focusable for whoever moves there.
  useEffect(() => {
    if (!openRequest) return;
    setOpen(true);
    window.setTimeout(() => {
      if (section.current && typeof section.current.scrollIntoView === 'function') section.current.scrollIntoView({ block: 'nearest' });
    }, 0);
  }, [openRequest]);

  const writesOpen = Boolean(cl && cl.writes_open !== false);
  const prior = cl ? cl.warnings.some((w) => w.code === 'PREVIOUSLY_SENT') : false;
  const unticked = ITEMS.filter(([k]) => !ticked[k]).map(([, label]) => label);
  const unconfirmedDocs = cl ? cl.required_attachments.filter((a) => a.present && !docs[a.document_class]).map((a) => a.label) : [];
  const blockers: string[] = [];
  if (cl) {
    for (const g of cl.gaps) blockers.push(g.message);
    if (unticked.length) blockers.push(`Tick: ${unticked.join(', ')}.`);
    if (to.size === 0) blockers.push('Choose at least one recipient.');
    if (unconfirmedDocs.length) blockers.push(`Confirm the attachment: ${unconfirmedDocs.join(', ')}.`);
    if (!subject.trim() || !body.trim()) blockers.push('The subject and the message need words.');
    if (/\{\{\s*[a-z_]+\s*\}\}/.test(`${subject}${body}`)) blockers.push('A {{placeholder}} is still in the email.');
    if (prior && !resend) blockers.push('An approval email was already sent (or may have been). Confirm below to send another.');
    if (!writesOpen) blockers.push('Sending approval emails is not switched on here.');
  }
  const ready = Boolean(cl) && blockers.length === 0;
  const canSend = ready && Boolean(cl && cl.send.enabled);

  const act = async (mode: 'SEND' | 'OUTLOOK') => {
    if (!cl || busy || !ready) return;
    if (mode === 'SEND' && !cl.send.enabled) return;
    setBusy(mode); setErr(null); setDone(null);
    const payload: ApprovalEmailSendBody = {
      recipients: cl.recipients.filter((r) => r.email && to.has(r.email)).map((r) => r.email as string),
      subject: subject.trim(), body,
      attachment_ids: cl.required_attachments.map((a) => docs[a.document_class]).filter((x): x is string => Boolean(x)),
      checklist_confirmed: { terms: ticked.terms, funds_manager: ticked.funds, poa: ticked.poa, facility_contacts: ticked.facility },
      idempotency_key: key.current, confirm_resend: resend, checklist_fingerprint: cl.fingerprint,
      body_version: draft ? draft.body_version : undefined,
    };
    try {
      const r = mode === 'SEND' ? await approvalEmailApi.send(caseId, payload) : await approvalEmailApi.eml(caseId, payload);
      if (!r.success || !r.data) {
        // A refusal sent nothing: the same key may be used again once she fixes it. A stale checklist is reloaded.
        // No answer at all may still have sent: the kept key makes the retry a replay, never a second email.
        setErr(r.status === undefined ? 'Could not reach the server. If you try again, eMerge will not send it twice.' : refusalText(r));
        if (r.code === 'CHECKLIST_CHANGED') void load();
        return;
      }
      // An answer -- any answer -- ends this act: the next click is a new one.
      key.current = freshKey();
      let msg = resultText(r.data);
      if (mode === 'OUTLOOK' && r.data.state === 'PREPARED_FOR_OUTLOOK' && !saveEml(r.data)) msg = 'The file was prepared but this browser could not save it. Try again from another browser.';
      setDone(msg);
      if (onChanged) await onChanged(msg);
      void load();
    } catch {
      // Unreached or unanswered: the key is kept, so trying again cannot send twice.
      setErr('Could not reach the server. If you try again, eMerge will not send it twice.');
    } finally { setBusy(null); }
  };

  return (
    <div className="um-section" ref={section} aria-label="Approval email">
      <h3 ref={heading} tabIndex={-1}>Approval email</h3>
      {!open && (
        <button type="button" className="um-open" aria-expanded={false} onClick={() => setOpen(true)}>Approval email</button>
      )}
      {open && (
        <>
          {loading && !cl && <p className="um-quiet" role="status">Loading the checklist…</p>}
          {loadErr && <p className="um-notice stop" role="alert">{loadErr}</p>}
          {cl && (
            <div className="um-form">
              <p className="um-quiet">
                Check each item, choose who it goes to, confirm the documents, and edit the words. Nothing is sent until you press Send
                {cl.send.enabled ? '' : ' (or open the downloaded file in Outlook and send it there)'}.
              </p>

              <fieldset className="um-check">
                <legend>Checklist</legend>
                <label>
                  <input type="checkbox" checked={ticked.terms} onChange={(e) => setTicked({ ...ticked, terms: e.target.checked })} />
                  {' '}Submitted terms: <strong>{cl.terms.text || 'none on record'}</strong>
                  {cl.terms.total_cents !== null && cl.terms.text && !cl.terms.text.includes('=') ? ` · total ${money(cl.terms.total_cents)}` : ''}
                  <span className="um-sub"> · {cl.terms.label}</span>
                </label>
                <label>
                  <input type="checkbox" checked={ticked.funds} disabled={cl.funds_manager.status === 'ACTION_REQUIRED'}
                    onChange={(e) => setTicked({ ...ticked, funds: e.target.checked })} />
                  {' '}Who manages the funds: <strong>{cl.funds_manager.status === 'ACTION_REQUIRED' ? `ACTION REQUIRED (${cl.funds_manager.label})` : cl.funds_manager.label}</strong>
                  {cl.funds_manager.source === 'MONDAY' ? <span className="um-sub"> · from the Monday board</span> : null}
                </label>
                <label>
                  <input type="checkbox" checked={ticked.poa} onChange={(e) => setTicked({ ...ticked, poa: e.target.checked })} />
                  {' '}POA: <strong>{cl.poa.name || 'not recorded'}</strong>{cl.poa.email ? ` · ${cl.poa.email}` : ' · no email'}{cl.poa.phone ? ` · ${cl.poa.phone}` : ''}
                  {(cl.responsible_party.name || cl.responsible_party.email || cl.responsible_party.phone || cl.responsible_party.address) && (
                    // SCRUM-132 (2026-11-03): the responsible party's phone and address (where invoices go) as well.
                    <span className="um-sub" data-testid="approval-email-rp"> · Responsible party: {cl.responsible_party.name || 'name not recorded'}{cl.responsible_party.email ? ` · ${cl.responsible_party.email}` : ''}{cl.responsible_party.phone ? ` · ${cl.responsible_party.phone}` : ''}{cl.responsible_party.address ? ` · Invoices to: ${cl.responsible_party.address}` : ''}</span>
                  )}
                </label>
                <label>
                  <input type="checkbox" checked={ticked.facility} onChange={(e) => setTicked({ ...ticked, facility: e.target.checked })} />
                  {' '}Facility: <strong>{cl.facility.name || 'not recorded'}</strong>
                  {' '}— {(['BOM', 'ABOM', 'SSD', 'ADMINISTRATOR'] as const).map((role) => {
                    const lines = cl.facility_contacts[role] || [];
                    const label = role === 'ADMINISTRATOR' ? 'Administrator' : role;
                    return `${label}: ${lines.length ? lines.map((l) => `${l.name || 'name not recorded'}${l.email ? ` <${l.email}>` : ' (no email)'}`).join(', ') : 'not recorded'}`;
                  }).join(' · ')}
                </label>
              </fieldset>

              <fieldset className="um-check">
                <legend>Send to</legend>
                {cl.recipients.length === 0 && <p className="um-quiet">Nobody is on record for this case.</p>}
                {cl.recipients.map((r) => (
                  <label key={r.key}>
                    <input type="checkbox" disabled={!r.email_present} checked={Boolean(r.email && to.has(r.email))}
                      onChange={(e) => {
                        const next = new Set(to);
                        if (r.email) { if (e.target.checked) next.add(r.email); else next.delete(r.email); }
                        setTo(next);
                      }} />
                    {' '}{r.label}: {r.name || 'name not recorded'}{r.title && r.title !== r.label ? ` (${r.title})` : ''} {r.email_present ? `<${r.email}>` : '· no email on record'}
                    {r.source === 'MONDAY' ? <span className="um-sub"> · from the Monday board, not confirmed in eMerge</span> : null}
                  </label>
                ))}
                {cl.sender.email && cl.send.enabled && <p className="um-sub">You are copied ({cl.sender.email}), and replies come to you.</p>}
              </fieldset>

              <fieldset className="um-check">
                <legend>Attachments</legend>
                {cl.required_attachments.length === 0 && <p className="um-quiet">Who manages the funds decides the documents. Record it first.</p>}
                {cl.required_attachments.map((a) => (
                  <div key={a.document_class}>
                    {!a.present && <p className="um-notice stop">Missing: {a.label}. Add it to the case’s documents first.</p>}
                    {a.candidates.map((d) => (
                      <label key={d.id}>
                        <input type="checkbox" checked={docs[a.document_class] === d.id}
                          onChange={(e) => setDocs({ ...docs, [a.document_class]: e.target.checked ? d.id : null })} />
                        {' '}Attach {a.label}{d.version ? ` v${d.version}` : ''}{d.original_filename ? ` · ${d.original_filename}` : ''} · {shortDateTime(d.created_at)}
                        {a.candidates.length > 1 && d.id === a.selected?.id ? <span className="um-sub"> · newest</span> : null}
                        {d.before_episode && d.warning ? <span className="um-sub"> · {d.warning}</span> : null}
                      </label>
                    ))}
                  </div>
                ))}
              </fieldset>

              <label className="um-termsfield">
                <span>Subject</span>
                <input type="text" value={subject} maxLength={250} onChange={(e) => setSubject(e.target.value)} aria-label="Subject" />
              </label>
              <label className="um-termsfield">
                <span>Message</span>
                <textarea value={body} rows={12} onChange={(e) => setBody(e.target.value)} aria-label="Message" />
              </label>

              {cl.warnings.filter((w) => w.code !== 'PREVIOUSLY_SENT').length > 0 && (
                <ul className="um-list" aria-label="Check these">
                  {cl.warnings.filter((w) => w.code !== 'PREVIOUSLY_SENT').map((w) => <li key={`${w.code}:${w.message}`} className="um-quiet">{w.message}</li>)}
                </ul>
              )}

              {cl.history.length > 0 && (
                <div>
                  <p className="um-sub">Earlier:</p>
                  <ul className="um-list" aria-label="Earlier approval emails">
                    {cl.history.map((h) => (
                      <li key={h.id}>{shortDateTime(h.requested_at)} · {h.mode === 'OUTLOOK' ? 'Downloaded for Outlook' : h.state === 'SENT' ? 'Sent' : h.state === 'UNKNOWN' ? 'Outcome unknown' : h.state === 'FAILED' ? 'Not sent' : 'Sending'}{h.actor_name ? ` by ${h.actor_name}` : ''} · to {h.to.map((x) => x.address).join(', ')}{(h.marked_during_send || []).length > 0 && h.mode === 'SEND' && (h.state === 'SENT' || h.state === 'UNKNOWN') ? <span className="um-quiet" role="note">{markedDuringSendText({ state: h.state, markedDuringSend: h.marked_during_send })}</span> : null}</li>
                    ))}
                  </ul>
                </div>
              )}
              {prior && (
                <label>
                  <input type="checkbox" checked={resend} onChange={(e) => setResend(e.target.checked)} />
                  {' '}I know an approval email was already sent (or may have been) for this case, and I mean to send another.
                </label>
              )}

              {blockers.length > 0 && (
                <div className="um-notice stop" role="status">
                  <p>Can’t send yet:</p>
                  <ul>{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
                </div>
              )}
              {!cl.send.enabled && cl.send.gaps.length > 0 && (
                <p className="um-quiet">{cl.send.gaps.map((g) => g.message).join(' ')}</p>
              )}
              {err && <p className="um-notice stop" role="alert">{err}</p>}
              {done && <p className="um-notice" role="status">{done}</p>}

              <div className="um-rowpanel-acts">
                {cl.send.enabled && (
                  <button type="button" className="um-open um-primary" aria-disabled={!canSend || busy !== null} disabled={!canSend}
                    onClick={() => void act('SEND')}>{busy === 'SEND' ? 'Sending…' : 'Send'}</button>
                )}
                <button type="button" className="um-open" disabled={!ready} aria-disabled={!ready || busy !== null}
                  onClick={() => void act('OUTLOOK')}>{busy === 'OUTLOOK' ? 'Preparing…' : 'Download for Outlook'}</button>
                <button type="button" className="um-link" disabled={busy !== null} onClick={() => setOpen(false)}>Close</button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
